/**
 * Who sees "Watch live" (ENG-2470), with the same gates as Live View on the
 * Alerts page:
 *
 *  - the user: the `showLiveStreaming` LaunchDarkly flag AND the login-time
 *    `live_streaming` permission (Alerts.tsx `showLiveStreaming`);
 *  - the site: `Customer.live_streaming_enabled`, the flag the stream mint
 *    gates on. Only an explicit `false` hides it, like
 *    `isLiveStreamingAvailableForAlert`; unknown is not "off".
 *
 * Integration types without video are gated by the callers (the camera check
 * shows per-camera actions only for video families; the camera page only for
 * sites that read the settings file). A non-prod build without "Use
 * production" is not hidden: the player says so, as Live View does.
 */
import React from "react";
import { useAppStore } from "../../../../appStore";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { fetchSiteConnectionDetail } from "../../Troubleshooting/TroubleshootingUtils/troubleshootingApi";

export const canUserWatchLive = (
  flags: Record<string, unknown> | null | undefined,
  permissions: Record<string, unknown> | null | undefined
): boolean =>
  Boolean(flags?.showLiveStreaming) && Boolean(permissions?.live_streaming);

export const isSiteLiveStreamingOn = (
  enabled: boolean | null | undefined
): boolean => enabled !== false;

/** The permission dict: the store, else the cached login (Troubleshooter). */
const useLiveStreamingPermission = (): boolean => {
  const fromStore = useAppStore((state) => state.userPermissions);
  if (fromStore?.live_streaming) return true;
  try {
    return Boolean(
      JSON.parse(localStorage.userInfo ?? "null")?.permission?.live_streaming
    );
  } catch {
    return false;
  }
};

export const useCanUserWatchLive = (): boolean => {
  const { flags } = useFeatureFlag();
  const permitted = useLiveStreamingPermission();
  return canUserWatchLive(flags, { live_streaming: permitted });
};

// One about/ read per site per page load, shared by every row's button.
const siteLiveStreamingCache = new Map<number, Promise<boolean | null>>();

/** Test hook: forget cached site answers. */
export const clearSiteLiveStreamingCache = () => siteLiveStreamingCache.clear();

const loadSiteLiveStreaming = (siteId: number): Promise<boolean | null> => {
  let pending = siteLiveStreamingCache.get(siteId);
  if (!pending) {
    pending = fetchSiteConnectionDetail(siteId)
      .then((about) => {
        const value = (about as { live_streaming_enabled?: boolean | null })
          ?.live_streaming_enabled;
        return typeof value === "boolean" ? value : null;
      })
      .catch(() => {
        // Unknown, not off; let a later mount try again.
        siteLiveStreamingCache.delete(siteId);
        return null;
      });
    siteLiveStreamingCache.set(siteId, pending);
  }
  return pending;
};

/**
 * The site's live_streaming_enabled: `known` when the caller already has the
 * site's About, else read once. `undefined` while loading (hide meanwhile),
 * `null` when unknown.
 */
export const useSiteLiveStreamingEnabled = (
  siteId: number | null | undefined,
  known: boolean | null | undefined,
  skip = false
): boolean | null | undefined => {
  const [loaded, setLoaded] = React.useState<boolean | null | undefined>(
    undefined
  );
  const needsFetch =
    !skip &&
    typeof known !== "boolean" &&
    siteId != null &&
    Number.isFinite(siteId);
  React.useEffect(() => {
    if (!needsFetch || siteId == null) return;
    let cancelled = false;
    setLoaded(undefined);
    loadSiteLiveStreaming(siteId).then((value) => {
      if (!cancelled) setLoaded(value);
    });
    return () => {
      cancelled = true;
    };
  }, [needsFetch, siteId]);
  if (typeof known === "boolean") return known;
  if (siteId == null || !Number.isFinite(siteId)) return null;
  return loaded;
};
